import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { financeReportsApi, type AdminDashboard, type DrillValue } from '../api/financeReports'
import {
  SCENARIO_LABELS, treasuryScenariosApi,
  type ScenarioCode, type SpendSimulation, type TreasuryScenarios,
} from '../api/treasuryScenarios'
import { EconomicSimulatorPanel } from '../components/EconomicSimulatorPanel'
import { useFeatureFlag } from '../lib/featureFlags'
import { dateIt, eur, errMsg, num, pct, shortDate, todayIso } from '../lib/financeFormat'
import {
  Button, ButtonGroup, Callout, Card, CardContent, CardHeader, ChartCard, DataTable, DateField, EmptyState, FormGrid,
  LineChart, LoadingState, Page, PageHeader, Select, StatCard, StatGrid, StatusPill, TextField,
} from '../components/ui'

/**
 * Blocco 1 / Card D (REQ-162-001, 167-001, 167-002, 195-001, 196-001, 198-001, 199-001, 200-003, 210-001) — cruscotto
 * amministrativo con importi: tessere `{value, drill}` cliccabili, i tre scenari di liquidita' con la riserva minima,
 * "quanto possiamo spendere" e simulatore economico. Nessun calcolo in pagina. Le cifre assenti (profilo senza
 * `CanSeeRealAmounts`) si mostrano come "—". Le imposte portano sempre l'etichetta STIMATO. Dietro i flag
 * `finanza.cruscotto_amministrativo`, `finanza.scenari_liquidita` e `finanza.simulatore`.
 */

interface Props {
  auth: LoginResponse
}

type Format = 'eur' | 'pct' | 'months'

interface TileDef {
  key: keyof AdminDashboard
  label: string
  format: Format
  note?: string
}

const TILES: TileDef[] = [
  { key: 'bank', label: 'Banca', format: 'eur' },
  { key: 'cash', label: 'Cassa', format: 'eur' },
  { key: 'freeLiquidity', label: 'Liquidità libera', format: 'eur', note: 'Dopo debiti, impegni e riserva' },
  { key: 'receivables', label: 'Crediti', format: 'eur' },
  { key: 'payables', label: 'Debiti', format: 'eur' },
  { key: 'forecastRevenue', label: 'Ricavi previsti', format: 'eur' },
  { key: 'accrued', label: 'Maturato', format: 'eur' },
  { key: 'collected', label: 'Incassato', format: 'eur' },
  { key: 'costsMonth', label: 'Costi del mese', format: 'eur' },
  { key: 'margin', label: 'Margine', format: 'eur' },
  { key: 'marginPct', label: 'Margine %', format: 'pct' },
  { key: 'in30', label: 'Entrate a 30 giorni', format: 'eur' },
  { key: 'out30', label: 'Uscite a 30 giorni', format: 'eur' },
  { key: 'runwayMonths', label: 'Autonomia', format: 'months' },
  { key: 'burnRate', label: 'Spesa mensile media', format: 'eur' },
  { key: 'budgetVsActual', label: 'Budget contro consuntivo', format: 'eur' },
  { key: 'partners', label: 'Saldi soci', format: 'eur' },
]

function formatValue(v: number | null | undefined, f: Format): string {
  if (v === null || v === undefined) return '—'
  if (f === 'pct') return pct(v)
  if (f === 'months') return `${num(v, 1)} mesi`
  return eur(v)
}

/** Il valore e', quando c'e' un drill, un link alla lista che lo compone (il totale della lista = il valore della tessera). */
function Tile({ def, cell, supporting }: { def: TileDef; cell: DrillValue | undefined; supporting?: ReactNode }) {
  const text = formatValue(cell?.value, def.format)
  const href = cell?.drill?.href
  return (
    <StatCard
      label={def.label}
      value={href && cell?.value !== null && cell?.value !== undefined ? <Link to={href}>{text}</Link> : text}
      supporting={supporting ?? def.note}
    />
  )
}

const HORIZONS = [30, 60, 90] as const

/**
 * IMP-3 — "riconosciuto dalla mandante": riga DISTINTA dalle tessere sopra. Sono i movimenti dei report della mandante
 * (pagato - storni + ricorrenti di Equal) dell'ultimo mese di report: NON e' incassato e non alimenta "Incassato" (l'incasso
 * vero e' quello bancario), cosi' lo stesso denaro non si conta due volte.
 */
function RecognizedByMandante({ cell }: { cell: NonNullable<AdminDashboard['recognizedByMandante']> }) {
  const month = cell.periodMonth ? new Date(cell.periodMonth + 'T00:00:00').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }) : null
  return (
    <StatGrid>
      <StatCard
        label="Riconosciuto mandante"
        value={cell.total === null ? '—' : eur(cell.total)}
        supporting={month
          ? `Report ${month}: netto ${eur(cell.net)} + ricorrenti ${eur(cell.recurring)}. Non è incasso.`
          : 'Nessun movimento della mandante importato.'}
      />
      {cell.queue > 0 && (
        <StatCard
          label="Da abbinare"
          value={<Link to={cell.drill.href}>{num(cell.queue)}</Link>}
          supporting="Movimenti della mandante senza pratica o da verificare"
        />
      )}
    </StatGrid>
  )
}

export function AdminCockpitPage({ auth }: Props) {
  const enabled = useFeatureFlag('finanza.cruscotto_amministrativo')
  const scenariosOn = useFeatureFlag('finanza.scenari_liquidita')
  const simulatorOn = useFeatureFlag('finanza.simulatore')
  const writerOrReader = auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione' || auth.role === 'EqualCommercialista'
  const [data, setData] = useState<AdminDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await financeReportsApi.adminDashboard())
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare il cruscotto.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (enabled) void load() }, [enabled, load])

  const taxesAmount = data?.taxes?.amount
  const taxesCell: DrillValue | undefined = typeof taxesAmount === 'number' ? { value: taxesAmount } : taxesAmount ?? undefined
  const amountsHidden = data !== null && TILES.every(t => data[t.key] === undefined)

  return (
    <Page>
      <PageHeader title="Cruscotto amministrativo" icon="dashboard_customize" actions={<Button variant="text" icon="refresh" loading={loading} onClick={() => void load()} disabled={!enabled}>Aggiorna</Button>} />
      {!enabled ? (
        <EmptyState icon="toggle_off" title="Funzione non attiva" description="Il cruscotto amministrativo non è ancora attivo per il tuo utente." />
      ) : (
        <>
          {!writerOrReader && <Callout tone="info">I dati sono riservati all'Amministrazione.</Callout>}
          {error && <Callout tone="error">{error}</Callout>}
          {loading && !data ? <LoadingState /> : data && (
            <>
              {amountsHidden && <Callout tone="info">Gli importi non sono visibili con il tuo profilo.</Callout>}
              <StatGrid>
                {TILES.filter(t => t.key !== 'partners' || data.partners !== undefined).map(t => (
                  <Tile key={t.key} def={t} cell={data[t.key] as DrillValue | undefined} />
                ))}
                <Tile
                  def={{ key: 'taxes', label: 'Imposte', format: 'eur' }}
                  cell={taxesCell}
                  supporting={<StatusPill tone="warning">{data.taxes?.label ?? 'STIMATO'}</StatusPill>}
                />
              </StatGrid>
              {data.recognizedByMandante && <RecognizedByMandante cell={data.recognizedByMandante} />}
            </>
          )}
          {scenariosOn && <ScenariosCard />}
          {scenariosOn && <SpendCard />}
          {simulatorOn && <EconomicSimulatorPanel />}
        </>
      )}
    </Page>
  )
}

function ScenariosCard() {
  const [days, setDays] = useState<number>(90)
  const [data, setData] = useState<TreasuryScenarios | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    void treasuryScenariosApi.scenarios(days, 'week')
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive) setError(errMsg(e, 'Scenari non disponibili.')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [days])

  const reference = data?.scenarios[0]
  const categories = reference ? reference.periods.map(p => shortDate(p.end)) : []
  const titles = reference ? reference.periods.map(p => `${dateIt(p.start)} – ${dateIt(p.end)}`) : []
  const reserve = data?.minimumReserve
  const series = [
    ...(data?.scenarios ?? []).map(s => ({
      id: s.code, name: SCENARIO_LABELS[s.code],
      color: s.code === 'prudente' ? 'error' as const : s.code === 'realistico' ? 'primary' as const : 'success' as const,
      values: s.periods.map(p => p.balance),
    })),
    ...(reserve !== undefined && reserve !== null && reference
      ? [{ id: 'riserva', name: 'Riserva minima', color: 'neutral' as const, values: reference.periods.map(() => reserve) }]
      : []),
  ]

  return (
    <ChartCard
      title="Liquidità prevista"
      subtitle="Tre scenari sui crediti attesi, con la riserva minima"
      legend={series.map(s => ({ label: s.name, color: s.color }))}
      actions={<ButtonGroup aria-label="Orizzonte" size="sm" options={HORIZONS.map(h => ({ value: String(h), label: `${h} giorni` }))} value={String(days)} onChange={v => setDays(Number(v))} />}
    >
      {error && <Callout tone="error">{error}</Callout>}
      {data && !data.available && (
        <Callout tone="info" title="Scenari non disponibili">{data.reason ?? 'La tesoreria non è raggiungibile in questo momento.'}</Callout>
      )}
      {(!data || data.available) && (
        <LineChart
          label="Liquidità prevista per scenario"
          categoryHeading="Fine periodo"
          unit="eur"
          loading={loading}
          categories={categories}
          categoryTitles={titles}
          series={series}
          emptyTitle="Nessuno scenario"
          emptyDescription="Gli importi non sono visibili con il tuo profilo o non ci sono ancora crediti attesi."
        />
      )}
      {data?.available && data.scenarios.length > 0 && (
        <DataTable variant="plain" aria-label="Sintesi degli scenari">
          <thead><tr><th>Scenario</th><th className="text-end">Saldo minimo</th><th>Sotto la riserva dal</th></tr></thead>
          <tbody>
            {data.scenarios.map(s => (
              <tr key={s.code}>
                <td>{SCENARIO_LABELS[s.code]}</td>
                <td className="text-end md-num">{eur(s.minBalance)}</td>
                <td>{s.breachDate ? <StatusPill tone="error">{dateIt(s.breachDate)}</StatusPill> : 'Mai nell’orizzonte'}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </ChartCard>
  )
}

function SpendCard() {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(todayIso())
  const [scenario, setScenario] = useState<ScenarioCode>('prudente')
  const [result, setResult] = useState<SpendSimulation | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [touched, setTouched] = useState(false)
  const invalid = !(Number(amount) > 0) || !date

  async function run(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (invalid) return
    setBusy(true)
    setError('')
    try {
      setResult(await treasuryScenariosApi.spendSimulation(Number(amount), date, scenario))
    } catch (x) {
      setResult(null)
      setError(errMsg(x, 'Simulazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Quanto possiamo spendere" subtitle="Una spesa ipotetica abbassa il saldo minimo dalla data indicata: nulla viene registrato." />
      <CardContent>
        <form onSubmit={e => void run(e)}>
          <FormGrid columns={3}>
            <TextField label="Importo" type="number" min={0} step="0.01" prefix="€" value={amount} error={touched && !(Number(amount) > 0) ? 'Indica un importo maggiore di zero.' : undefined} onChange={e => setAmount(e.target.value)} />
            <DateField kind="date" label="A partire dal" value={date} onChange={e => setDate(e.target.value)} />
            <Select label="Scenario" value={scenario} onChange={e => setScenario(e.target.value as ScenarioCode)}>
              {(Object.keys(SCENARIO_LABELS) as ScenarioCode[]).map(c => <option key={c} value={c}>{SCENARIO_LABELS[c]}</option>)}
            </Select>
          </FormGrid>
          <Button variant="filled" type="submit" icon="payments" loading={busy}>Simula la spesa</Button>
        </form>
        {error && <Callout tone="error">{error}</Callout>}
        {result && (
          <StatGrid>
            <StatCard label="Saldo minimo prima" value={eur(result.before.minBalance)} supporting={result.before.breachDate ? `Sotto la riserva dal ${dateIt(result.before.breachDate)}` : 'Sempre sopra la riserva'} />
            <StatCard
              label="Saldo minimo dopo" value={eur(result.after.minBalance)}
              tone={result.after.breachDate ? 'error' : 'success'}
              supporting={result.after.breachDate ? `Sotto la riserva dal ${dateIt(result.after.breachDate)}` : 'Sempre sopra la riserva'}
            />
          </StatGrid>
        )}
      </CardContent>
    </Card>
  )
}
